import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Loader2, Share2, Image as ImageIcon, FileText
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Brain Gym Jr. logo (small mark, top-left of the card)
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_event-coordinator-9/artifacts/0pz7v8ll_Pink%20and%20Teal%20Playful%20Exercise%20Brain%20T%20Shirt%20%2810%20x%2010%20in%29%20%282%29.png';

// 5 rotating kid-friendly phrases for participation certs.
// We pick deterministically from participant_id so the same kid always sees
// the same phrase (good for re-downloads).
const PARTICIPATION_PHRASES = [
  'AMAZING EFFORT!',
  "YOU'RE A STAR!",
  'WELL PLAYED!',
  'BRAIN POWER UNLOCKED!',
  'BIG BRAIN ENERGY!',
];

const phraseFor = (participantId = '') => {
  let h = 0;
  for (let i = 0; i < participantId.length; i++) h = (h * 31 + participantId.charCodeAt(i)) & 0xffffffff;
  return PARTICIPATION_PHRASES[Math.abs(h) % PARTICIPATION_PHRASES.length];
};

// Deterministic Memphis-style geometric cluster — uses ONLY explicit shapes (no <pattern> fills),
// so html2canvas captures it cleanly without losing the design on PNG/PDF export.
const MemphisCluster = ({ palette, variant = 'tl' }) => {
  const isBR = variant === 'br';
  // BR cluster is smaller (and pushed further out) than TL so it doesn't overlap the
  // bottom-right SIGNATURE area but still echoes the Memphis vibe.
  const w = isBR ? 460 : 560;
  const h = isBR ? 460 : 560;

  // Build explicit dot grid (replaces SVG <pattern fill="dots">)
  const dots = [];
  for (let y = 0; y < 11; y++) {
    for (let x = 0; x < 11; x++) {
      const cx = 165 + x * 12;
      const cy = 265 + y * 12;
      const dist = Math.sqrt((cx - 220) ** 2 + (cy - 320) ** 2);
      if (dist <= 55) dots.push(<circle key={`d-${x}-${y}`} cx={cx} cy={cy} r="2.2" fill={palette[2]} />);
    }
  }

  // Striped half-circle (explicit diagonal lines)
  const stripeLines1 = [];
  for (let i = 0; i < 10; i++) {
    const off = i * 9;
    stripeLines1.push(
      <line key={`s1-${i}`} x1={180 + off} y1={80} x2={260} y2={160 + off - 90} stroke={palette[1]} strokeWidth="3" />
    );
  }
  // Striped diamond — explicit diagonal lines clipped to a rotated square
  const stripeLines2 = [];
  for (let i = 0; i < 8; i++) {
    const off = i * 8;
    stripeLines2.push(
      <line key={`s2-${i}`} x1={325 + off} y1={435} x2={325 + off + 50} y2={485} stroke={palette[2]} strokeWidth="2.5" opacity="0.85" />
    );
  }

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      style={{
        position: 'absolute',
        ...(isBR ? { bottom: -310, right: -310 } : { top: -150, left: -150 }),
        pointerEvents: 'none',
        transform: isBR ? 'rotate(180deg)' : 'none',
      }}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Outline ring (yellow) */}
      <circle cx="190" cy="200" r="200" stroke={palette[0]} strokeWidth="3" fill="none" opacity="0.7" />
      {/* Big yellow circle */}
      <circle cx="140" cy="160" r="140" fill={palette[0]} />
      {/* Striped half-circle (coral) */}
      <g clipPath="url(#half-clip)">{stripeLines1}</g>
      <defs>
        <clipPath id="half-clip">
          <path d={`M 180 80 A 80 80 0 0 1 260 160 L 180 160 Z`} />
        </clipPath>
      </defs>
      {/* Concentric rings (coral) */}
      <circle cx="430" cy="430" r="65" stroke={palette[1]} strokeWidth="3" fill="none" />
      <circle cx="430" cy="430" r="45" stroke={palette[1]} strokeWidth="3" fill="none" />
      <circle cx="430" cy="430" r="25" stroke={palette[1]} strokeWidth="3" fill="none" />
      {/* Dotted circle — explicit dots */}
      {dots}
      {/* Navy solid circle */}
      <circle cx="350" cy="240" r="38" fill={palette[3]} />
      {/* Small triangle (teal) */}
      <polygon points="280,60 320,130 240,130" fill={palette[2]} />
      {/* Small triangle (coral) */}
      <polygon points="420,90 445,140 395,140" fill={palette[1]} />
      {/* Small solid coral dot */}
      <circle cx="380" cy="350" r="20" fill={palette[1]} />
      {/* Tiny yellow square */}
      <rect x="460" y="150" width="22" height="22" fill={palette[0]} transform="rotate(20 471 161)" />
      {/* Outline diamond (navy) */}
      <rect x="370" y="380" width="80" height="80" stroke={palette[3]} strokeWidth="3" fill="none" transform="rotate(45 410 420)" />
      {/* Striped diamond (teal) — explicit diagonal lines */}
      <g transform="rotate(45 350 460)">{stripeLines2}</g>
    </svg>
  );
};

const CertificatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [certificate, setCertificate] = useState(null);
  const [participantId, setParticipantId] = useState('');
  const [busy, setBusy] = useState('');  // '', 'png', 'pdf', 'share'
  const [shareMsg, setShareMsg] = useState('');
  // Cert signature settings — fetched from public endpoint so they're admin-editable.
  const [certSettings, setCertSettings] = useState({ signature_name: 'S Das', director_title: 'Brain Gym Jr. Director' });
  // Anti-cheat badge — HMAC code + verify URL (used to render the QR and the small VERIFY text).
  const [badge, setBadge] = useState({ code: '', verify_url: '' });

  useEffect(() => {
    axios.get(`${API_URL}/api/cert-settings`)
      .then(r => setCertSettings({
        signature_name: r.data?.signature_name || 'S Das',
        director_title: r.data?.director_title || 'Brain Gym Jr. Director',
      }))
      .catch(() => {});
  }, []);

  // Issue (or fetch existing) badge once we know the participant_id.
  useEffect(() => {
    if (!participantId) return;
    axios.post(`${API_URL}/api/badge/issue`, { participant_id: participantId })
      .then(r => {
        const code = r.data?.code || '';
        if (code) {
          setBadge({
            code,
            verify_url: `${window.location.origin}/verify-cert?code=${encodeURIComponent(code)}`,
          });
        }
      })
      .catch(() => {});
  }, [participantId]);
  const cardRef = useRef(null);

  useEffect(() => {
    const pid = searchParams.get('participant_id');
    if (pid) {
      setParticipantId(pid);
      loadCertificate(pid);
    } else {
      const t = setTimeout(() => setLoading(false), 0);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadCertificate = async (pid) => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/certificate/${pid}`);
      setCertificate(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load certificate');
    } finally {
      setLoading(false);
    }
  };

  const handleLookup = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (participantId) {
      navigate(`/certificate?participant_id=${participantId}`);
      loadCertificate(participantId);
    }
  };

  // Generate a 2160×2160 canvas (2× DPI of 1080) — sharp for retina + social uploads.
  const renderCanvas = async () => {
    if (!cardRef.current) return null;
    // Wait for Google Fonts (Fredoka, Great Vibes) to fully load before capture,
    // otherwise html2canvas snapshots fallback fonts and the design "disappears".
    try {
      if (document?.fonts?.ready) {
        await document.fonts.ready;
        // Explicit font preload to nudge browser into making sure the actual @font-face URL is fetched.
        await Promise.all([
          document.fonts.load('700 78px Fredoka').catch(() => null),
          document.fonts.load('400 120px "Great Vibes"').catch(() => null),
          document.fonts.load('italic 700 56px "Great Vibes"').catch(() => null),
        ]);
      }
      // Tiny extra frame so any layout reflow from fonts settles before capture.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    } catch { /* non-fatal */ }
    return html2canvas(cardRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: null,
      logging: false,
      // Capture the SVG cleanly — force foreignObject off (which html2canvas mis-handles).
      foreignObjectRendering: false,
    });
  };

  const safeFileBase = () => {
    const isWinner = certificate.certificate_type === 'winner';
    const safeName = (certificate.participant_name || 'participant').replace(/[^a-z0-9]+/gi, '-');
    return `BrainGym-Olympiad-${isWinner ? 'Winner' : 'Participation'}-${safeName}`;
  };

  const downloadPNG = async () => {
    setBusy('png'); setError('');
    try {
      const canvas = await renderCanvas();
      if (!canvas) return;
      const dataUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${safeFileBase()}.png`;
      a.click();
    } catch (e) {
      setError("Couldn't save the image. Try a different browser.");
    } finally { setBusy(''); }
  };

  const downloadPDF = async () => {
    setBusy('pdf'); setError('');
    try {
      const canvas = await renderCanvas();
      if (!canvas) return;
      const imgData = canvas.toDataURL('image/png');
      // Sticker is square (200×200mm), Memphis cert is landscape (297×186mm).
      const pdf = isWinner
        ? new jsPDF({ orientation: 'landscape', unit: 'mm', format: [297, 186] })
        : new jsPDF({ orientation: 'portrait', unit: 'mm', format: [200, 200] });
      const w = isWinner ? 297 : 200;
      const h = isWinner ? 186 : 200;
      pdf.addImage(imgData, 'PNG', 0, 0, w, h);
      pdf.save(`${safeFileBase()}.pdf`);
    } catch (e) {
      setError("Couldn't generate the PDF.");
    } finally { setBusy(''); }
  };

  // Try Web Share API (mobile native sheet) → falls back to Facebook share URL
  const shareCertificate = async () => {
    setBusy('share'); setError(''); setShareMsg('');
    try {
      const canvas = await renderCanvas();
      if (!canvas) return;
      const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
      const file = new File([blob], `${safeFileBase()}.png`, { type: 'image/png' });
      const isWinner = certificate.certificate_type === 'winner';
      const text = isWinner
        ? `${certificate.participant_name} earned ${certificate.title} at ${certificate.event_name}! 🏆`
        : `${certificate.participant_name} just completed ${certificate.event_name}! ⭐`;

      // Best path: native share sheet with the image attached (works on most phones)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'My Brain Gym Jr. Certificate', text });
        setShareMsg('Shared!');
      } else if (navigator.share) {
        // No file sharing — share text + URL
        await navigator.share({ title: 'My Brain Gym Jr. Certificate', text, url: window.location.href });
        setShareMsg('Shared!');
      } else {
        // Desktop fallback — open Facebook share dialog with the page URL.
        // We also download the PNG so the user can attach it manually.
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/png');
        a.download = `${safeFileBase()}.png`;
        a.click();
        const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}&quote=${encodeURIComponent(text)}`;
        window.open(fbUrl, '_blank', 'width=600,height=500');
        setShareMsg('Saved image + opened Facebook — attach it to your post!');
      }
      setTimeout(() => setShareMsg(''), 4000);
    } catch (e) {
      if (e?.name !== 'AbortError') setError('Sharing was cancelled or not supported.');
    } finally { setBusy(''); }
  };

  // ====== STATES (loading / lookup / error) ======

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#2565eb] animate-spin mx-auto mb-3" />
          <p className="text-[#64748b]">Loading your certificate…</p>
        </div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
        <header className="p-6 flex items-center border-b border-[#e2e8f0]">
          <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))} className="flex items-center gap-2 text-[#64748b] hover:text-[#0f172a]" data-testid="cert-back-btn">
            <ArrowLeft className="w-5 h-5" /> Back
          </button>
        </header>
        <main className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-md w-full">
            <h2 className="text-2xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>View your certificate</h2>
            <p className="text-sm text-[#64748b] mb-5">Enter your Participant ID to view & download your certificate.</p>
            {error && <div className="bg-[#fef2f2] border-2 border-[#fecaca] text-[#dc2626] px-4 py-3 rounded-2xl mb-4 text-sm" data-testid="cert-error">{error}</div>}
            <form onSubmit={handleLookup} className="space-y-3">
              <input
                value={participantId}
                onChange={(e) => setParticipantId(e.target.value)}
                placeholder="Participant ID"
                className="w-full px-4 py-3 rounded-2xl border-2 border-[#e2e8f0] focus:border-[#2565eb] outline-none"
                data-testid="certificate-participant-input"
              />
              <button type="submit" className="btn-primary w-full" data-testid="view-certificate-button">View Certificate</button>
            </form>
          </div>
        </main>
      </div>
    );
  }

  // ====== CERTIFICATE DISPLAY ======

  const isWinner = certificate.certificate_type === 'winner';
  const rank = certificate.rank;

  // Memphis theme — cream background with rank-specific accent palette.
  // palette = [yellow-equivalent, coral-equivalent, teal-equivalent, navy/text]
  const theme = (() => {
    if (rank === 1) return {
      bg: '#fffdf5',
      navy: '#2D3C6C',
      script: '#E75950',
      tagline: 'CHAMPION',
      palette: ['#F6C955', '#E75950', '#0EA5A4', '#2D3C6C'],
    };
    if (rank === 2) return {
      bg: '#fffdf5',
      navy: '#2D3C6C',
      script: '#475569',
      tagline: '1ST RUNNER-UP',
      palette: ['#CBD5E1', '#94A3B8', '#64748B', '#2D3C6C'],
    };
    if (rank === 3) return {
      bg: '#fffdf5',
      navy: '#2D3C6C',
      script: '#B45309',
      tagline: '2ND RUNNER-UP',
      palette: ['#FBBF24', '#D97706', '#92400E', '#2D3C6C'],
    };
    // Participation — full Memphis palette (matches the uploaded reference)
    return {
      bg: '#fffdf5',
      navy: '#2D3C6C',
      script: '#E75950',
      tagline: phraseFor(certificate.participant_name + (certificate.certificate_id || '')),
      palette: ['#F6C955', '#E75950', '#0EA5A4', '#2D3C6C'],
    };
  })();

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="certificate-page">
      <header className="p-4 sm:p-5 flex items-center justify-between border-b border-[#e2e8f0] bg-white sticky top-0 z-10">
        <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))} className="flex items-center gap-2 text-[#64748b] hover:text-[#0f172a] text-sm" data-testid="cert-back-btn">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>
        <div className="flex flex-wrap gap-2 justify-end">
          <button
            onClick={shareCertificate}
            disabled={!!busy}
            className="px-3.5 py-2 rounded-xl text-xs sm:text-sm font-bold bg-[#2565eb] text-white hover:bg-[#1e40af] transition-all flex items-center gap-1.5 disabled:opacity-60"
            data-testid="share-certificate-button"
          >
            {busy === 'share' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            Share
          </button>
          <button
            onClick={downloadPNG}
            disabled={!!busy}
            className="px-3.5 py-2 rounded-xl text-xs sm:text-sm font-bold border-2 border-[#16a34a] text-[#16a34a] hover:bg-[#dcfce7] transition-all flex items-center gap-1.5 disabled:opacity-60"
            data-testid="download-png-button"
          >
            {busy === 'png' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
            Save Image
          </button>
          <button
            onClick={downloadPDF}
            disabled={!!busy}
            className="px-3.5 py-2 rounded-xl text-xs sm:text-sm font-bold border-2 border-[#e2e8f0] text-[#64748b] hover:border-[#2565eb] hover:text-[#2565eb] transition-all flex items-center gap-1.5 disabled:opacity-60"
            data-testid="download-pdf-button"
          >
            {busy === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            PDF
          </button>
        </div>
      </header>

      {error && <div className="bg-[#fef2f2] border-2 border-[#fecaca] text-[#dc2626] px-4 py-3 mx-4 mt-4 rounded-xl text-sm" data-testid="cert-error">{error}</div>}
      {shareMsg && <div className="bg-[#dcfce7] border-2 border-[#86efac] text-[#14532d] px-4 py-3 mx-4 mt-4 rounded-xl text-sm font-bold" data-testid="cert-share-msg">{shareMsg}</div>}

      <main className="p-3 sm:p-6 flex justify-center">
        {/* PARTICIPATION → render a compact 800×800 round sticker badge.
            WINNER (rank ≤ 3) → render the existing 1600×1000 Memphis landscape cert. */}
        {!isWinner ? (
          <div className="cert-sticker-wrapper">
            <div
              ref={cardRef}
              data-testid="certificate-card"
              style={{
                position: 'relative',
                width: '800px', height: '800px',
                borderRadius: '50%',
                background: 'radial-gradient(circle at center, #fffdf5 0%, #fef3c7 100%)',
                boxShadow: '0 25px 60px rgba(15,23,42,0.22)',
                flexShrink: 0,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                overflow: 'hidden',
              }}
            >
              {/* Outer ring */}
              <div style={{ position: 'absolute', inset: 24, border: `6px solid ${theme.navy}`, borderRadius: '50%', pointerEvents: 'none' }} />
              {/* Inner ring (coral, dashed effect via gap) */}
              <div style={{ position: 'absolute', inset: 48, border: `3px dashed ${theme.script}`, borderRadius: '50%', pointerEvents: 'none' }} />
              {/* Star sprinkles around the rim */}
              {[0,1,2,3,4,5,6,7,8,9,10,11].map((i) => {
                const angle = (i * 30) * Math.PI / 180;
                const r = 360;
                const x = 400 + r * Math.cos(angle) - 14;
                const y = 400 + r * Math.sin(angle) - 14;
                return (
                  <svg key={i} width="28" height="28" viewBox="0 0 28 28" style={{ position: 'absolute', left: x, top: y }}>
                    <polygon points="14,2 17,11 26,11 19,17 22,26 14,20 6,26 9,17 2,11 11,11" fill={i % 2 === 0 ? theme.palette[0] : theme.script} />
                  </svg>
                );
              })}

              {/* Logo top — generous vertical breathing room so the yellow logo disc
                  and the BRAIN GYM JR. wordmark read as one tidy lockup. */}
              <div style={{
                position: 'absolute', top: 78, left: '50%', transform: 'translateX(-50%)',
                textAlign: 'center', zIndex: 2,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
              }}>
                <img
                  src={LOGO_URL}
                  alt="Brain Gym Jr."
                  crossOrigin="anonymous"
                  style={{ width: 100, height: 100, objectFit: 'contain', display: 'block' }}
                />
                <div style={{
                  fontSize: 15, fontWeight: 800, color: theme.navy,
                  letterSpacing: '4px', lineHeight: 1,
                  fontFamily: '"Fredoka", "Helvetica Neue", Helvetica, Arial, sans-serif',
                }}>
                  BRAIN GYM JR.
                </div>
              </div>

              {/* PARTICIPATED — bold center label */}
              <div style={{
                position: 'absolute', top: 220, left: '50%', transform: 'translateX(-50%)',
                width: '80%', textAlign: 'center', zIndex: 2,
              }}>
                <div style={{ fontSize: 26, fontWeight: 700, color: theme.script, letterSpacing: '8px' }}>
                  PARTICIPATED
                </div>
                <div style={{ width: 80, height: 2, background: theme.navy, opacity: 0.4, margin: '14px auto 18px' }} />
                <div style={{
                  fontFamily: '"Great Vibes", "Brush Script MT", cursive',
                  fontSize: 78, color: theme.script, lineHeight: 1, padding: '0 30px 14px',
                  wordBreak: 'break-word',
                }}>
                  {certificate.participant_name}
                </div>
                <div style={{ fontSize: 14, color: theme.navy, opacity: 0.8, letterSpacing: '1px', marginTop: 6 }}>
                  participated in the
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: theme.navy, marginTop: 4 }}>
                  {certificate.event_name || 'Weekly Brain Challenge'}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: theme.navy, opacity: 0.85, marginTop: 4 }}>
                  on {certificate.event_date && new Date(certificate.event_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              </div>

              {/* QR + verify code bottom-center */}
              {badge.code && (
                <div style={{
                  position: 'absolute', bottom: 70, left: '50%', transform: 'translateX(-50%)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, zIndex: 5,
                }}>
                  <div style={{ background: 'white', padding: 4, borderRadius: 6, lineHeight: 0 }}>
                    <QRCodeSVG value={badge.verify_url} size={68} level="M" includeMargin={false} />
                  </div>
                  <div style={{ fontSize: 9, fontFamily: 'monospace', color: theme.navy, opacity: 0.6, letterSpacing: '1px' }}>
                    VERIFY · {badge.code}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
        <div className="cert-card-wrapper">
          <div
            ref={cardRef}
            data-testid="certificate-card"
            style={{
              position: 'relative',
              width: '1600px',
              height: '1000px',
              background: theme.bg,
              overflow: 'hidden',
              borderRadius: '24px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              boxShadow: '0 25px 60px rgba(15,23,42,0.18)',
              flexShrink: 0,
            }}
          >
            {/* Memphis-style geometric clusters — top-left + bottom-right (mirrored) */}
            <MemphisCluster palette={theme.palette} variant="tl" />
            <MemphisCluster palette={theme.palette} variant="br" />

            {/* Brand mark — UPPER CENTER, above the CERTIFICATE title */}
            <div style={{
              position: 'absolute', top: 40, left: '50%', transform: 'translateX(-50%)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, zIndex: 2,
            }}>
              <img
                src={LOGO_URL}
                alt="Brain Gym Jr."
                crossOrigin="anonymous"
                style={{ width: 78, height: 78, objectFit: 'contain' }}
              />
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.navy, letterSpacing: '2px' }}>
                BRAIN GYM JR.
              </div>
            </div>

            {/* CENTERED HEADER + RECIPIENT + BODY — uses absolute centering inside the middle width.
                Nudged slightly down (~4%) to clear the upper-center logo. */}
            <div style={{
              position: 'absolute', top: '54%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '60%', textAlign: 'center', zIndex: 1,
            }}>
              {/* CERTIFICATE title */}
              <div style={{
                fontFamily: '"Fredoka", -apple-system, sans-serif',
                fontSize: 78, fontWeight: 700, color: theme.navy,
                letterSpacing: '4px', lineHeight: 1, marginBottom: 14,
              }}>
                CERTIFICATE
              </div>

              {/* Divider + OF ACHIEVEMENT */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 14, marginBottom: 22,
              }}>
                <div style={{ width: 60, height: 2, background: theme.navy, opacity: 0.6 }} />
                <div style={{
                  fontSize: 18, fontWeight: 700, color: theme.navy,
                  letterSpacing: '6px',
                }}>
                  {isWinner ? `OF ${theme.tagline}` : 'OF PARTICIPATION'}
                </div>
                <div style={{ width: 60, height: 2, background: theme.navy, opacity: 0.6 }} />
              </div>

              {/* Presented to */}
              <div style={{
                fontSize: 16, fontWeight: 600, color: theme.navy,
                letterSpacing: '4px', marginBottom: 38, opacity: 0.85,
              }}>
                THIS CERTIFICATE IS PROUDLY PRESENTED TO
              </div>

              {/* Recipient name — script font, large, coral.
                  Extra bottom padding so script descenders (y, g) don't overlap the body line. */}
              <div style={{
                fontFamily: '"Great Vibes", "Brush Script MT", cursive',
                fontSize: 120, fontWeight: 400, color: theme.script,
                lineHeight: 1.0, paddingBottom: 30, marginBottom: 32,
                wordBreak: 'break-word',
              }}>
                {certificate.participant_name}
              </div>

              {/* Minimal body line */}
              <div style={{
                fontSize: 18, color: theme.navy, lineHeight: 1.5,
                opacity: 0.82, maxWidth: '85%', margin: '0 auto',
              }}>
                {isWinner
                  ? `Awarded for outstanding performance at ${certificate.event_name}.`
                  : `Awarded for participating in ${certificate.event_name}.`}
              </div>
            </div>

            {/* Bottom row: DATE (left) + SIGNATURE (right).
                Per request: line removed above DATE; line kept above SIGNATURE with
                an italic "S Das" signature mark sitting on it. */}
            <div style={{
              position: 'absolute', bottom: 140, left: 0, right: 0,
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
              padding: '0 90px', zIndex: 5,
            }}>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: theme.navy, letterSpacing: '3px' }}>DATE</div>
                <div style={{ fontSize: 12, color: theme.navy, opacity: 0.7, marginTop: 2 }}>
                  {certificate.event_date && new Date(certificate.event_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {/* Signature name — admin-editable via /admin/settings → italic script sitting just above the line */}
                <div style={{
                  fontFamily: '"Great Vibes", "Brush Script MT", cursive',
                  fontStyle: 'italic',
                  fontSize: 44, color: theme.navy, lineHeight: 1, marginBottom: 4,
                  textAlign: 'right',
                }}>
                  {certSettings.signature_name}
                </div>
                <div style={{ width: 180, height: 1.5, background: theme.navy, opacity: 0.4, marginBottom: 8, marginLeft: 'auto' }} />
                <div style={{ fontSize: 14, fontWeight: 700, color: theme.navy, letterSpacing: '3px' }}>SIGNATURE</div>
                <div style={{ fontSize: 12, color: theme.navy, opacity: 0.7, marginTop: 2 }}>
                  {certSettings.director_title}
                </div>
              </div>
            </div>

            {/* QR verification block — bottom-center, small. Anti-cheat anchor: anyone scanning
                lands on /verify-cert and sees the real owner's name + date from the DB. */}
            {badge.code && (
              <div
                style={{
                  position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
                  display: 'flex', alignItems: 'center', gap: 10, zIndex: 5,
                }}
              >
                <div style={{ textAlign: 'right', fontSize: 10, color: theme.navy, opacity: 0.6, lineHeight: 1.3 }}>
                  <div style={{ fontWeight: 700, letterSpacing: '1px' }}>SCAN TO VERIFY</div>
                  <div style={{ fontFamily: 'monospace', marginTop: 2 }}>{badge.code}</div>
                </div>
                <div style={{ background: 'white', padding: 4, borderRadius: 6, lineHeight: 0 }}>
                  <QRCodeSVG value={badge.verify_url} size={70} level="M" includeMargin={false} />
                </div>
              </div>
            )}

            {/* Cert ID — bottom-left tiny */}
            <div style={{
              position: 'absolute', bottom: 24, left: 24,
              fontSize: 9, fontFamily: 'monospace', color: theme.navy, opacity: 0.35, zIndex: 2,
            }}>
              {certificate.certificate_id}
            </div>
          </div>
        </div>
        )}
      </main>

      <p className="text-center text-xs text-[#94a3b8] pb-6 px-4">
        Tap <strong>Share</strong> to post on Facebook / WhatsApp · <strong>Save Image</strong> for a PNG · <strong>PDF</strong> for printing.
      </p>

      {/* Responsive scale. Card is ALWAYS 1600×1000 for html2canvas. Wrapper scales the visual.
          Aspect ratio = 1.6:1 (landscape). The calc() pattern divides length-by-length to yield
          a unitless ratio that transform:scale() accepts. */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Great+Vibes&family=Fredoka:wght@400;600;700&display=swap');

        .cert-card-wrapper {
          width: 1600px;
          height: 1000px;
          overflow: visible;
        }
        .cert-card-wrapper > div {
          transform-origin: top left;
        }
        @media (max-width: 1660px) {
          .cert-card-wrapper {
            width: min(95vw, 1100px);
            height: calc(min(95vw, 1100px) * 0.625);
          }
          .cert-card-wrapper > div {
            transform: scale(min(calc(95vw / 1600px), calc(1100 / 1600)));
          }
        }

        /* Round sticker — fixed 800×800, scales down for mobile via transform. */
        .cert-sticker-wrapper {
          width: 800px;
          height: 800px;
          overflow: visible;
        }
        .cert-sticker-wrapper > div {
          transform-origin: top left;
        }
        @media (max-width: 860px) {
          .cert-sticker-wrapper {
            width: min(92vw, 600px);
            height: min(92vw, 600px);
          }
          .cert-sticker-wrapper > div {
            transform: scale(min(calc(92vw / 800px), calc(600 / 800)));
          }
        }
      `}</style>
    </div>
  );
};

export default CertificatePage;
